import React, {useMemo} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import AssetErrorsNotificationBanner from '@contentlab/components/asset-errors-notification-banner';
import {AssetErrors} from '@contentlab/stores/contentLabStore';
import {FeatureName} from '@contentlab/types/project';
import translate from '@/utils/translate';
import Guide from './Guide';

const EmptyUploadContainer = styled(Stack, {
  shouldForwardProp: (prop: string) => prop !== 'bigTopPadding',
})(({bigTopPadding}: {bigTopPadding: boolean}) => ({
  alignItems: 'center',
  borderRadius: '32px',
  border: `1px dashed ${designSystemToken('semantic.fg.tertiary')}`,
  background: designSystemToken('semantic.bg.primary'),
  flex: 1,
  minHeight: 0,
  paddingBlock: bigTopPadding ? '56px' : '20px',
}));

const Content = styled(Stack)(() => ({
  justifyContent: 'center',
  gap: '48px',
  height: '100%',
}));

const TopStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: '36px',
  gap: '16px',
}));

const BottomStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
}));

const UploadIconContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CtaSectionContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifySelf: 'center',
  gap: '8px',
}));

const Separator = styled(Box)(() => ({
  'display': 'flex',
  'alignItems': 'center',
  'width': '270px',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: designSystemToken('semantic.fg.tertiary'),
  },
}));

const SeparatorText = styled(Body)(() => ({
  margin: '0 16px',
  color: designSystemToken('semantic.fg.tertiary'),
}));

interface EmptyUploadProps {
  testID?: string;
  title: string;
  guideTitle: string;
  guideDescription: string;
  onLearnMoreClick?: () => void;
  assetErrors: AssetErrors[];
  isLoading?: boolean;
  feature?: FeatureName | string;
}

function EmptyUpload({
  testID = 'empty-uploader',
  title,
  guideTitle,
  guideDescription,
  onLearnMoreClick,
  assetErrors,
  isLoading = false,
  feature,
}: EmptyUploadProps) {
  const errorCodes = useMemo(() => {
    if (assetErrors.length) {
      return assetErrors[0].errorCodes;
    }
    return null;
  }, [assetErrors]);

  return (
    <EmptyUploadContainer
      direction="column"
      gap="10px"
      bigTopPadding={assetErrors.length === 0}
    >
      {errorCodes ? (
        <Box
          paddingBottom="28px"
          width="100%"
          paddingInline="20px"
          boxSizing="border-box"
        >
          <AssetErrorsNotificationBanner
            errorCodes={errorCodes}
            feature={feature}
          />
        </Box>
      ) : null}
      <Content>
        <TopStack>
          <Headline size="md" color={designSystemToken('semantic.fg.primary')}>
            {translate(title)}
          </Headline>
          <UploadIconContainer>
            <Icon
              name="Actions-Upload"
              size="medium"
              appearance="primary"
              style={{alignSelf: 'center'}}
            />
          </UploadIconContainer>
          <Button
            appearance="neutral"
            mode="filled"
            size="large"
            sx={{width: '300px', height: '48px'}}
            isLoading={isLoading}
          >
            {translate('Browse files')}
          </Button>
          <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Or drag and drop here')}
          </Body>
        </TopStack>
        <BottomStack>
          <Guide
            guideTitle={guideTitle}
            guideDescription={guideDescription}
            onLearnMoreClick={onLearnMoreClick}
            sx={{width: 'fit-content', whiteSpace: 'pre-wrap', gap: '16px'}}
            textSx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          />
        </BottomStack>
      </Content>
    </EmptyUploadContainer>
  );
}

export default EmptyUpload;
